import React from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Button, CircularProgress, Grid, Paper } from '@mui/material'
import { Check, Close, LocationOn } from '@mui/icons-material'

import { trackGiftSearchButtonClick } from 'utils/mixpanel'

import Pills from 'components/Pills'
import { getDeadlineState, getNextDeadlinesFromPrograms } from 'components/Deadlines/helpers'
import Notes from './components/Notes'
import GiftInfo from './components/GiftInfo'
import PipelineStageButton from 'components/PipelineStageButton'
import CardActions from 'components/CardActions'
import ResetCardAction from 'components/CardActions/actions/ResetAction'
import ArchiveCardAction from 'components/CardActions/actions/ArchiveAction'
import HideCardAction from 'components/CardActions/actions/HideAction'

import { getFunderById } from 'store/actions/profile'
import {
  selectFunderPills,
  selectFunderProfileCategory,
  selectFunderProfileInfo,
  selectIsFunderProfileLoading,
  selectFunderPrecalculatedInfo
} from 'store/selectors/profile'
import RequestSize from './components/RequestSize'
import SpecialNote from './components/SpecialNote'
import Deadline from 'components/Deadlines'
import { selectIsLibraryMode } from 'store/selectors/user'
import NotEngagedNote from './components/NotEngagedNote'

const ProfileCard = ({ isDialog }) => {
  const { i18n } = useTranslation()
  const t = i18n.getResourceBundle(i18n.language)
  const isLoading = useSelector(selectIsFunderProfileLoading)
  const {
    id,
    name,
    websiteUrl,
    openToRequests,
    funderPrograms,
    address,
    funderOrganizationFunderOpportunity,
    funderActivityOption,
    primaryCategory
  } = useSelector(selectFunderProfileInfo)

  const { medianGiftSize, giftsLastYear, medianRecipientSize } =
    useSelector(selectFunderPrecalculatedInfo) || {}

  const pillItems = useSelector(selectFunderPills)
  const category = useSelector(selectFunderProfileCategory)
  const isLibraryMode = useSelector(selectIsLibraryMode)

  const funderHasNoPrograms = funderPrograms ? funderPrograms.length === 0 : true
  const { hasOngoingDeadline, closestDeadlineDate } = getNextDeadlinesFromPrograms(funderPrograms)

  const {
    color: cardColor,
    Icon,
    label,
    showDate
  } = getDeadlineState({
    deadlineDate: closestDeadlineDate,
    isOngoing: hasOngoingDeadline,
    hasNoPrograms: funderHasNoPrograms
  })

  let requestStatusIcon = ''
  let requestStatus = ''
  if (openToRequests === 'yes') {
    requestStatusIcon = <Check />
    requestStatus = t.search.openRequests
  }
  if (openToRequests === 'no') {
    requestStatusIcon = <Close />
    requestStatus = t.search.closeRequests
  }

  const giftInfos = [
    { label: t.cards.giftsLastYear, value: giftsLastYear },
    { label: t.cards.typicalGift, value: medianGiftSize },
    { label: t.cards.typicalRecipientSize, value: medianRecipientSize }
  ]

  const showResetCardAction =
    funderOrganizationFunderOpportunity &&
    (funderOrganizationFunderOpportunity.pipelineStage ||
      funderOrganizationFunderOpportunity.archived ||
      funderOrganizationFunderOpportunity.hidden)

  const showArchiveCardAction =
    funderOrganizationFunderOpportunity &&
    funderOrganizationFunderOpportunity.pipelineStage &&
    !funderOrganizationFunderOpportunity.hidden &&
    !funderOrganizationFunderOpportunity.archived

  const showHideCardAction =
    !funderOrganizationFunderOpportunity || !funderOrganizationFunderOpportunity.hidden

  const isFunderFoundation =
    !!primaryCategory?.root?.name && primaryCategory.root.name === t.categories.foundations
  const isFunderGrantmaking = funderActivityOption
    ? funderActivityOption.some(
        activity =>
          activity.name === t.activities.grantmaking ||
          activity.name === t.activities.grantmaking.toLowerCase()
      )
    : false

  const handleOpenFullProfile = async () => {
    window.open(`/profile/${id}`)
    await trackGiftSearchButtonClick({ button: 'table_popup.funder.full_profile' })
  }

  const Container = isDialog ? 'div' : Paper

  return (
    <Grid size={{ xs: 12 }} className="Profile-card">
      {isLoading ? (
        <div className="refresh-container">
          <CircularProgress size={60} thickness={5} color="#4c9eff" />
        </div>
      ) : (
        <React.Fragment>
          <Container className={`Full-card ${cardColor}`}>
            <Grid container>
              <Grid size={{ xs: 12, md: 7, lg: 8 }}>
                <Grid container columnSpacing={3}>
                  <Grid size={{ xs: 12, sm: 'auto' }}>
                    <h1 className="tw-text-xl">{name}</h1>
                    <small className="Full-card__type tw-text-sm">{category}</small>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 'auto' }}>
                    {websiteUrl && (
                      <Button
                        className="Full-card__website"
                        onClick={() => window.open(websiteUrl)}
                        variant="text"
                      >
                        {t.cards.website}
                      </Button>
                    )}
                  </Grid>
                </Grid>

                <Grid container>
                  <GiftInfo giftInfos={giftInfos} />
                </Grid>

                <Grid container>
                  <Grid size={{ xs: 12 }}>
                    <div className="Full-card__open-to-requests">
                      <div className="tw-flex tw-items-center tw-mt-2">
                        <small className="tw-text-sm">{`${t.funder.funderPrograms}: ${funderPrograms.length}`}</small>
                        {requestStatusIcon}
                        <small className="tw-text-sm">{requestStatus} </small>
                        <LocationOn /> <small className="tw-text-sm"> {address} </small>
                      </div>
                    </div>
                  </Grid>
                </Grid>

                <Grid container>
                  <Grid size={{ xs: 12 }}>
                    <div className={`Full-card__deadline ${cardColor}`}>
                      {!!Icon && <Icon />}
                      <small>
                        {label}
                        {showDate && <Deadline date={closestDeadlineDate} format="long" />}
                      </small>
                    </div>
                  </Grid>
                </Grid>
              </Grid>

              <Grid size={{ xs: 12, md: 5, lg: 4 }}>
                <Grid container>
                  <Grid size={{ xs: 12 }}>
                    <div className="Full-card__actions">
                      {!isLibraryMode && !isDialog && (
                        <RequestSize opportunity={funderOrganizationFunderOpportunity} />
                      )}
                      <PipelineStageButton
                        buttonClass="Full-card__status"
                        opportunity={funderOrganizationFunderOpportunity}
                        funderId={id}
                        loadData={() => getFunderById(id, false)}
                      />
                      <CardActions
                        funder={{ id }}
                        opportunityId={
                          funderOrganizationFunderOpportunity &&
                          funderOrganizationFunderOpportunity.id
                        }
                        actions={[
                          showResetCardAction && ResetCardAction,
                          showArchiveCardAction && ArchiveCardAction,
                          showHideCardAction && HideCardAction
                        ]}
                        loadData={() => getFunderById(id, false)}
                      />
                    </div>
                  </Grid>
                </Grid>
                {!isDialog && (
                  <Grid container>
                    <Grid size={{ xs: 12 }}>
                      <Notes profileId={id} />
                    </Grid>
                  </Grid>
                )}
              </Grid>
            </Grid>
            {isDialog && (
              <Grid container className="tw-items-end tw-mt-2">
                <Grid size={{ xs: 12, md: 4 }}>
                  <Button
                    className="button-primary"
                    onClick={handleOpenFullProfile}
                    color="primary"
                    variant="text"
                  >
                    {t.explorer.full_profile}
                  </Button>
                </Grid>
                <Grid size={{ xs: 12, md: 8 }}>
                  <Notes profileId={id} inDialog />
                </Grid>
              </Grid>
            )}
          </Container>
          {!isDialog && (
            <>
              <Pills items={pillItems} areRemovable={false} selectOnClick renderInfoSection />
              <NotEngagedNote />
              <SpecialNote />
            </>
          )}
        </React.Fragment>
      )}
    </Grid>
  )
}

export default ProfileCard
